import axios from 'src/store/vue-axios/axios.js'
import GlobalPrototypes from '@/config/services/globalPrototypes.ts'

export default class Users {
  // ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  // API ENDPOINTS ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  // ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

  // USERS -> /users*
  static allUsersUrl = () => '/users'
  static userChannelrableUrl = () => '/users/channelrable'
  static userContactableUrl = () => '/users/contactable'
  static sendPushUrl = () => '/users/send-push'

  // USERS -> /users/*
  static recoverUserUrl = () => '/users/recover'
  static userByGradeUrl = (gradeId) => `/users/?grade_id=${gradeId}&limit=500`

  // USERS -> /users/self/*
  static selfUrl = () => '/users/self'
  static selfFamilyUrl = () => '/users/self/family'
  static selfFeedCommentsUrl = () => '/users/self/feed/comments'
  static selfTimetableSubjectColorUrl = () => '/users/self/timetable/subject-colors'
  static selfTimetableCoursesUrl = () => '/users/self/timetable/courses'
  static selfTimetableLessonsUrl = () => '/users/self/timetable/lessons'
  static selfPostableChannelsUrl = () => '/users/self/channels/postable'
  static personalNotificationsUrl = () => '/users/self/channels/personal-notifications'
  static activitySummaryUrl = () => '/users/self/activity-summary'

  // USERS -> /users/${userId}*
  static singleUserUrl = (userId) => `/users/${userId}`
  static recoverSingleUserUrl = () => '/users/recover'
  static deleteUserManualUrl = () => '/users/hard-delete'
  static educationUrl = (userId) => `/users/${userId}/education`
  static softDeleteUserUrl = (userId) => `/users/${userId}/delete`
  static initiateDeletionRequestUrl = (userId) => `/users/${userId}/request-deletion`
  static cancelDeletionRequestUrl = (userId) => `/users/${userId}/cancel-deletion`
  static approveDeletionRequestUrl = (userId) => `/users/${userId}/approve-deletion`
  static deletionStatusUrl = (userId) => `/users/${userId}/deletion-status`
  static moveSchoolUrl = (userId) => `/users/${userId}/move-school`
  static rolesUrl = (userId) => `/users/${userId}/roles`
  static rolesAvailableUrl = (userId) => `/users/${userId}/roles/available`
  static permissionsUrl = (userId) => `/users/${userId}/permissions`
  static permissionsAvailableUrl = (userId) => `/users/${userId}/permissions/available`
  static twoFactorAuthUrl = (userId) => `/users/${userId}/2fa`
  static personalDataUrl = (userId) => `/users/${userId}/personal-data`
  static usernameSuggestionUrl = (userId) => `/users/${userId}/username-suggestion`
  static setupUrl = (userId) => `/users/${userId}/setup`
  static familyUrl = (userId) => `/users/${userId}/family`
  static devicesUrl = (userId) => `/users/${userId}/devices`
  static updatePasswordUrl = (userId) => `/users/${userId}/update-password`
  static updateNameUrl = (userId) => `/users/${userId}/update-name`
  static updateEmailUrl = (userId) => `/users/${userId}/update-email`
  static updateUsernameUrl = (userId) => `/users/${userId}/update-username`
  static changeStatisticUrl = (userId) => `/users/${userId}/change-statistics`
  static undoExpireUrl = (userId) => `/users/${userId}/undo-expire`
  static updateLocaleUrl = (userId) => `/users/${userId}/update-locale`
  static forceLogoutFromAllDevicesUrl = (userId) => `/users/${userId}/force-logout-all`

  // PARENTS -> /users/${userId}/parents*
  static removeParentUrl = (userId) => `/users/${userId}/parents/remove`
  static pendingParentUrl = (userId) => `/users/${userId}/parents/pending`

  // TEACHING -> /users/${userId}/teaching*
  static teachingUrl = (userId) => `/users/${userId}/teaching`
  static coTeachingUrl = (userId) => `/users/${userId}/teaching/co`

  // SETTINGS -> /users/${userId}/settings*
  static settingsUrl = (userId) => `/users/${userId}/settings`
  static notificationSettingsUrl = (userId) => `/users/${userId}/settings/pause-notifications`

  // CHANNELS -> /users/${userId}/channels*
  static channelsUrl = (userId) => `/users/${userId}/channels`
  static channelsPaginatedSearchUrl = (userId, page, searchVal) => `/users/${userId}/channels?search=${searchVal}&page=${page}&hide_conversations=true`
  static channelsPaginatedUrl = (userId, page) => `/users/${userId}/channels?page=${page}`
  static channelsCloudUrl = (userId) => `/users/${userId}/channels/clouds?`
  static channelsCloudOrderedUrl = (userId, orderBy, orderDir) => `/users/${userId}/channels/clouds?order-by=${orderBy}&order-dir=${orderDir}&with_inactive=true&`
  static channelsArchivedUrl = (userId) => `/users/${userId}/channels/archived`
  static channelsDisabledUrl = (userId) => `/users/${userId}/channels/disabled`
  static channelsMutedUrl = (userId) => `/users/${userId}/channels/muted`
  static channelsInactiveUrl = (userId) => `/users/${userId}/channels/chats?only_inactive=1`
  static channelsNewsUrl = (userId) => `/users/${userId}/channels/news`
  static channelsChatPaginatedSearchUrl = (userId, page, searchVal) => `/users/${userId}/channels/chats?page=${page}&search=${searchVal}&limit=10`
  static channelsChatPaginatedSearchWithArchiveUrl = (userId, page, searchVal) => `/users/${userId}/channels/chats?with_archived=true&page=${page}&search=${searchVal}&limit=10`
  static channelsUnreadChatPaginatedSearchUrl = (userId, page, searchVal) => `/users/${userId}/channels/chats?only_unread=1&page=${page}&search=${searchVal}&limit=10`
  static channelsMessageableUrl = (userId) => `/users/${userId}/channels/messageable?`

  // CHILDS -> /users/${userId}/childs*
  static assignChildUrl = (userId) => `/users/${userId}/childs`
  static removeChildUrl = (userId) => `/users/${userId}/childs/remove`
  static checkCodeChildUrl = (userId) => `/users/${userId}/childs/check-code`
  static requestCodeChildUrl = (userId) => `/users/${userId}/childs/request-code`

  // FEED -> /users/${userId}/feed*
  static newsFeedPaginatedUrl = (userId, page, search = '') => `/users/${userId}/feed/news?page=${page}&search=${search}`
  static newsFeedPaginatedScheduledUrl = (userId, page) => `/users/${userId}/feed/news?page=${page}&is_scheduled=true`
  static newsFeedPaginatedUpdatedAtUrl = (userId, page, date) => `/users/${userId}/feed/news?page=${page}&updated_at=${date}`
  static newsFeedCommentsUrl = (userId) => `/users/${userId}/feed/comments`

  // TIMETABLE -> /users/${userId}/timetable*
  static ttUrl = (userId) => `/users/${userId}/timetable`
  static ttCoursesUrl = (userId) => `/users/${userId}/timetable/courses`
  static ttCoursesResetUrl = (userId) => `/users/${userId}/timetable/courses/reset`
  static ttCoursesColorUrl = (userId) => `/users/${userId}/timetable/course-colors`
  static ttGradeUrl = (userId) => `/users/${userId}/timetable/grade`
  static ttAvailableGradeUrl = (userId) => `/users/${userId}/timetable/grade/available`

  // SHORTCUT -> /users/${userId}/shortcut*
  static shortcutUrl = (userId) => `/users/${userId}/shortcut`
  static availableShortcutUrl = (userId) => `/users/${userId}/shortcut/available`

  // PUSH
  static pushTestUrl = (userId) => `/users/${userId}/push-test`
  static pushExtraUrl = (cacheToken, deviceToken) => `/push/push-details?cache_token=${cacheToken}&device_token=${deviceToken}`
  static revokePushTokenUrl = (userId, token) => `/devices/revoke?user_id=${userId}&token=${token}`

  // ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  // API IMPLEMENTATIONS ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  // ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

  // USERS -> /users*
  static async getAllUsers (params) {
    return axios.get(this.allUsersUrl(), params)
  }

  static async postSendPush (payload) {
    return axios.post(this.sendPushUrl(), payload)
  }

  // USERS -> /users/*
  static async putRecoverUser (userIds) {
    return axios.put(this.recoverUserUrl(), { users: userIds })
  }

  static async getUserByGrade (gradeId) {
    return axios.get(this.userByGradeUrl(gradeId))
  }

  // USERS -> /users/self/*
  static async getSelfUser () {
    return GlobalPrototypes.cacheHttp(this.selfUrl())
  }

  static async getSelfFamily () {
    return axios.get(this.selfFamilyUrl())
  }

  static async postSelfTimetableSubjectColor (payload, config) {
    return axios.post(this.selfTimetableSubjectColorUrl(), payload, config)
  }

  // USERS -> /users/${userId}*
  static async postTwoFactorAuth (userId, payload) {
    return axios.post(this.twoFactorAuthUrl(userId), payload)
  }

  static async postPersonalData (userId) {
    return axios.post(this.personalDataUrl(userId))
  }

  static async getUsernameSuggestion (userId) {
    return axios.get(this.usernameSuggestionUrl(userId))
  }

  static async putSetup (userId, payload) {
    return axios.put(this.setupUrl(userId), payload)
  }

  static async getFamily (userId) {
    return axios.get(this.familyUrl(userId))
  }

  static async postDevices (userId, payload, config) {
    return axios.post(this.devicesUrl(userId), payload, config)
  }

  static async putUpdatePassword (userId, payload) {
    return axios.put(this.updatePasswordUrl(userId), payload)
  }

  static async putUpdateName (userId, payload) {
    return axios.put(this.updateNameUrl(userId), payload)
  }

  static async putUpdateEmail (userId, payload) {
    return axios.put(this.updateEmailUrl(userId), payload)
  }

  static async putUpdateUsername (userId, payload) {
    return axios.put(this.updateUsernameUrl(userId), payload)
  }

  static async putChangeStatistic (userId, payload, config) {
    return axios.put(this.changeStatisticUrl(userId), payload, config)
  }

  static async postUndoExpire (userId) {
    return axios.post(this.undoExpireUrl(userId))
  }

  static async putUpdateLocale (userId, payload) {
    return axios.put(this.updateLocaleUrl(userId), payload)
  }

  static async getSingleUser (userId) {
    return axios.get(this.singleUserUrl(userId))
  }

  static async putSingleUser (userId, payload, config) {
    return axios.put(this.singleUserUrl(userId), payload, config)
  }

  static async putRecoverSingleUserUrl (users) {
    return axios.put(this.recoverSingleUserUrl(), { users })
  }

  static async postUserDeletionRequest (userId, payload) {
    return axios.post(this.initiateDeletionRequestUrl(userId), payload)
  }

  static async putRejectUserDeletionRequest (userId) {
    return axios.put(this.cancelDeletionRequestUrl(userId))
  }

  static async putApproveUserDeletionRequest (userId) {
    return axios.put(this.approveDeletionRequestUrl(userId))
  }

  static async getUserDeletionRequestStatus (userId) {
    return axios.get(this.deletionStatusUrl(userId))
  }

  static async deleteUserManual (user_id) {
    return axios.delete(this.deleteUserManualUrl(), { params: { user_id } })
  }

  static async getEducation (userId) {
    return axios.get(this.educationUrl(userId))
  }

  static async deleteUserSoft (userId, payload) {
    return axios.delete(this.softDeleteUserUrl(userId), { params: payload })
  }

  static async putMoveSchool (userId, payload, config) {
    return axios.put(this.moveSchoolUrl(userId), payload, config)
  }

  static async putRoles (userId, payload, config) {
    return axios.put(this.rolesUrl(userId), payload, config)
  }

  static async getRoles (userId) {
    return axios.get(this.rolesUrl(userId))
  }

  static async getRolesAvailable (userId) {
    return axios.get(this.rolesAvailableUrl(userId))
  }

  static async putPermissions (userId, payload, config) {
    return axios.put(this.permissionsUrl(userId), payload, config)
  }

  static async getPermissions (userId) {
    return axios.get(this.permissionsUrl(userId))
  }

  static async getPermissionsAvailable (userId) {
    return axios.get(this.permissionsAvailableUrl(userId))
  }

  static async forceLogoutFromAllDevices (userId) {
    return axios.post(this.forceLogoutFromAllDevicesUrl(userId))
  }

  // PARENTS -> /users/${userId}/parents*
  static async postRemoveParent (childId, parentId, config) {
    return axios.post(this.removeParentUrl(childId), { parent_id: parentId }, config)
  }

  static async getPendingParent (userId) {
    return axios.get(this.pendingParentUrl(userId), { showMessages: false })
  }

  // TEACHING -> /users/${userId}/teaching*
  static async getTeaching (userId) {
    return axios.get(this.teachingUrl(userId))
  }

  static async putTeaching (userId, payload) {
    return axios.put(this.teachingUrl(userId), payload)
  }

  static async putCoTeaching (userId, payload) {
    return axios.put(this.coTeachingUrl(userId), payload)
  }

  // SETTINGS -> /users/${userId}/settings*
  static async getSettings (userId) {
    return axios.get(this.settingsUrl(userId), { showMessages: false })
  }

  static async putSettings (userId, payload, config) {
    return axios.put(this.settingsUrl(userId), payload, config)
  }

  static async getNotificationSettings (userId) {
    return axios.get(this.notificationSettingsUrl(userId), { showMessages: false })
  }

  static async deleteNotificationSettings (userId, config) {
    return axios.delete(this.notificationSettingsUrl(userId), config)
  }

  static async putNotificationSettings (userId, payload) {
    return axios.put(this.notificationSettingsUrl(userId), payload)
  }

  // CHANNELS -> /users/${userId}/channels*
  static async getChannelsPaginatedSearch (userId, page, searchVal) {
    return axios.get(this.channelsPaginatedSearchUrl(userId, page, searchVal))
  }

  static async getChannelsPaginated (userId, page) {
    return axios.get(this.channelsPaginatedUrl(userId, page))
  }

  static async getChannelsMuted (userId) {
    return axios.get(this.channelsMutedUrl(userId))
  }

  static async getChannelsInactive (userId) {
    return axios.get(this.channelsInactiveUrl(userId))
  }

  static async getChannelsNews (userId, params) {
    return axios.get(this.channelsNewsUrl(userId), params)
  }

  static async getChannelsChatPaginatedSearch (userId, page, searchVal) {
    return GlobalPrototypes.cacheHttp(this.channelsChatPaginatedSearchUrl(userId, page, searchVal))
  }

  static async getChannelsChatPaginatedSearchWithArchive (userId, page, searchVal) {
    return GlobalPrototypes.cacheHttp(this.channelsChatPaginatedSearchWithArchiveUrl(userId, page, searchVal))
  }

  static async getChannelsUnreadChatPaginatedSearch (userId, page, searchVal) {
    return GlobalPrototypes.cacheHttp(this.channelsUnreadChatPaginatedSearchUrl(userId, page, searchVal))
  }

  static async getChannelsPostable () {
    return axios.get(this.selfPostableChannelsUrl())
  }

  static async getPersonalNotifications () {
    return axios.get(this.personalNotificationsUrl())
  }

  static async getActivitySummary () {
    return axios.get(this.activitySummaryUrl())
  }

  // CHILDS -> /users/${userId}/childs*
  static async postAssignChild (parentId, childId) {
    return axios.post(this.assignChildUrl(parentId), { user_id: childId })
  }

  static async postAssignChildAsObject (parentId, child) {
    return axios.post(this.assignChildUrl(parentId), child)
  }

  static async postRemoveChild (parentId, childId) {
    return axios.post(this.removeChildUrl(parentId), { child_id: childId })
  }

  static async putAssignChild (parentId, child) {
    return axios.put(this.assignChildUrl(parentId), child)
  }

  static async postCheckCodeChild (userId, payload) {
    return axios.post(this.checkCodeChildUrl(userId), payload)
  }

  static async postRequestCodeChild (userId) {
    return axios.post(this.requestCodeChildUrl(userId))
  }

  // FEED -> /users/${userId}/feed*
  static async getNewsFeedPaginatedUpdatedAt (userId, page, date, config) {
    return axios.get(this.newsFeedPaginatedUpdatedAtUrl(userId, page, date), config)
  }

  static async getNewsFeedComments (userId) {
    return axios.get(this.newsFeedCommentsUrl(userId))
  }

  static async getNewsFeedPaginated (userId, page) {
    return GlobalPrototypes.cacheHttp(this.newsFeedPaginatedUrl(userId, page))
  }

  static async getNewsFeedPaginatedScheduled (userId, page) {
    return GlobalPrototypes.cacheHttp(this.newsFeedPaginatedScheduledUrl(userId, page))
  }

  // TIMETABLE -> /users/${userId}/timetable*
  static async getTimetable (userId) {
    return GlobalPrototypes.cacheHttp(this.ttUrl(userId))
  }

  static async putTtCourses (userId, payload, config) {
    return axios.put(this.ttCoursesUrl(userId), payload, config)
  }

  static async postTtCoursesReset (userId) {
    return axios.post(this.ttCoursesResetUrl(userId))
  }

  static async postTtCoursesColor (userId, payload, config) {
    return axios.post(this.ttCoursesColorUrl(userId), payload, config)
  }

  static async putTtGrade (userId, payload) {
    return axios.put(this.ttGradeUrl(userId), payload)
  }

  static async getTtAvailableGrades (userId) {
    return axios.get(this.ttAvailableGradeUrl(userId))
  }

  // SHORTCUT -> /users/${userId}/shortcut*
  static async putShortcut (userId, payload) {
    return axios.put(this.shortcutUrl(userId), payload)
  }

  static async getAvailableShortcut (userId) {
    return axios.get(this.availableShortcutUrl(userId))
  }

  static async sendPushTest (userId) {
    return axios.post(this.pushTestUrl(userId))
  }

  static async getPushExtra (cacheToken, deviceToken) {
    return axios.get(this.pushExtraUrl(cacheToken, deviceToken))
  }

  static async deleteUserPushToken (userId, token) {
    return axios.delete(this.revokePushTokenUrl(userId, token))
  }
}
